import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router'

import { useConnections } from 'app/connections/application'
import { mapToConnectionName, mapToUrl } from 'app/connections/presentation/connectionUrlMapper'
import { useObjectives } from 'app/objectives/application'
import { useSignals } from 'app/signals/application'

import type { BreadcrumbPath } from 'components/Breadcrumb/Breadcrumb'

const createPath = (name: string, path?: string): BreadcrumbPath => ({ name, path })

const useBreadcrumbPaths = (): BreadcrumbPath[] => {
  const { pathname } = useLocation()
  const params = useParams()
  const { t: tDashboard } = useTranslation('dashboard')
  const { t: tHeader } = useTranslation('sidebar')
  const { t: tConnections } = useTranslation('connections')
  const { t: tObjectives } = useTranslation('objectives')
  const { t: tSettings } = useTranslation('settings')
  const { t: tSignals } = useTranslation('signals')
  const { getObjective } = useObjectives()
  const { getConnection } = useConnections()
  const { signals } = useSignals()

  if (pathname === '/') {
    return [createPath(tDashboard('breadcrumb'))]
  }

  if (pathname.startsWith('/events')) {
    return [createPath(tHeader('menu.events'))]
  }

  if (pathname === '/connections/browse') {
    return [
      createPath(tConnections('overview.headline'), '/connections'),
      createPath(tConnections('overview.browse.breadcrumb')),
    ]
  }

  if (pathname.startsWith('/connections/') && params.name) {
    const connection = getConnection(mapToConnectionName(params.name))
    const connectionName = connection
      ? tConnections(`available.${connection.name}.displayName`)
      : params.name

    if (pathname.includes('/connect')) {
      return [
        createPath(tConnections('overview.headline'), '/connections'),
        createPath(connectionName, `/connections/${mapToUrl(mapToConnectionName(params.name))}`),
        createPath(tConnections('setup.connect')),
      ]
    }

    return [
      createPath(tConnections('overview.headline'), '/connections'),
      createPath(connectionName),
    ]
  }

  if (pathname.startsWith('/connections')) {
    return [createPath(tConnections('overview.headline'))]
  }

  if (pathname === '/objectives/create') {
    return [
      createPath(tHeader('menu.objectives'), '/objectives'),
      createPath(tObjectives('create.breadcrumb')),
    ]
  }

  if (pathname.startsWith('/objectives/') && params.id) {
    const objective = getObjective(params.id)

    return [
      createPath(tHeader('menu.objectives'), '/objectives'),
      createPath(objective?.name ?? params.id),
    ]
  }

  if (pathname.startsWith('/objectives')) {
    return [createPath(tHeader('menu.objectives'))]
  }

  if (pathname.startsWith('/settings')) {
    const setting = pathname.split('/')[2]

    return [
      createPath(tSettings('title'), '/settings/user'),
      createPath(setting ? tSettings(`menu.${setting}`) : tSettings('title')),
    ]
  }

  if (pathname.startsWith('/signals/new')) {
    return [createPath(tHeader('menu.signals'), '/signals'), createPath(tSignals('create.title'))]
  }

  if (pathname.startsWith('/signals')) {
    if (params.id) {
      return [
        createPath(tHeader('menu.signals'), '/signals'),
        createPath(signals.find((signal) => signal.id === params.id)?.name ?? params.id),
      ]
    }
    return [createPath(tHeader('menu.signals'))]
  }

  return []
}

export default useBreadcrumbPaths
